import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import html2canvas from 'html2canvas';
import 'chartjs-adapter-date-fns';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import {
  IInnovationMetricsHistoryPoint,
  IKeywords,
} from '../../../../../../core/models/results.model';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { SIGNAL_COLORS } from '../../../../../../shared/utils/bracket.utils';

type ScoreSecondaryAxis = 'size' | 'growth' | 'quality' | 'rank' | 'abs_growth'|null;
type TimeRange = '10y' | '3y' | '1y';

interface ScoreAxisCfg {
  label: string;
  field: keyof IInnovationMetricsHistoryPoint;
  color: string;
  type: 'scatter' | 'bar';
  mode?: 'lines+markers';
  hoverSuffix?: string;
  opacity?: number;
}

const SCORE_SECONDARY_AXIS_CONFIG: Record<Exclude<ScoreSecondaryAxis, null>, ScoreAxisCfg> = {
  abs_growth: { label: 'Abs. Growth', field: 'absolute_growth_12m', color: '#a2c613', type: 'bar',     opacity: 0.45, hoverSuffix: ' Inventions' },
  growth:     { label: 'Rel. Growth', field: 'relative_growth_12m', color: '#f59e0b', type: 'bar',     opacity: 0.45, hoverSuffix: '%' },
  size:       { label: 'Size',        field: 'invention_count_120m', color: '#22c55e', type: 'bar',     opacity: 0.4 },
  quality:    { label: 'Quality',     field: 'quality_score_12m',    color: '#a855f7', type: 'scatter', mode: 'lines+markers' },
  rank:       { label: 'Percentile Rank',        field: 'percentile_rank',      color: '#0f766e', type: 'scatter', mode: 'lines+markers' },
};

const FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const COLOR = {
  axisText: '#64748b',
  axisTitle: '#64748b',
  tooltipBg: '#ffffff',
  tooltipTitle: '#64748b',
  tooltipBody: '#0f172a',
  tooltipBorder: 'rgba(15, 23, 42, 0.08)',
  grid: 'rgba(15, 23, 42, 0.05)',
  chartAreaBg: '#f8fafc',
  scoreLine: '#1e293b',
};

const whiteBackgroundPlugin = {
  id: 'innovScoreWhiteBg',
  beforeDraw(chart: Chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

const chartAreaBackgroundPlugin = {
  id: 'innovScoreChartAreaBg',
  beforeDatasetsDraw(chart: Chart) {
    const { left, top, width, height } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.fillStyle = COLOR.chartAreaBg;
    ctx.fillRect(left, top, width, height);
    ctx.restore();
  },
};

Chart.register(...registerables, zoomPlugin, annotationPlugin);

const DAY_MS = 86_400_000;
const MIN_ZOOM_RANGE_MS = 30 * DAY_MS;
const AXIS_PAD_DAYS = 10;

const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
const addDays = (d: Date, days: number): Date => {
  const c = new Date(d);
  c.setDate(c.getDate() + days);
  return c;
};

// Bars/markers are positioned at the 1st of the month so they align with
// month-start ticks instead of sitting one day before the next month's tick
// (date_eom '2026-03-31' would otherwise render under the 'Apr 26' label).
const toMonthStart = (dateEom: string): string => `${dateEom.slice(0, 7)}-01`;

@Component({
  selector: 'app-innovation-activity-over-time',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, BaseChartDirective],
  templateUrl: './innovation-activity-over-time.component.html',
  styleUrl: './innovation-activity-over-time.component.css',
})
export class InnovationActivityOverTimeComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() query: string | null = null;

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;
  @ViewChild('exportTarget') private exportTarget?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();

  history: IInnovationMetricsHistoryPoint[] = [];

  chartjsData!: ChartConfiguration<'bar'>['data'];
  chartjsOptions!: ChartConfiguration<'bar'>['options'];
  readonly chartPlugins = [whiteBackgroundPlugin, chartAreaBackgroundPlugin];

  showScoreChart = false;
  showScoreSpinner = false;
  scoreLoadingFailed = false;
  scoreDataMissing = false;

  secondaryAxis: ScoreSecondaryAxis = 'abs_growth';
  timeRange: TimeRange = '10y';

  readonly SIGNAL = SIGNAL_COLORS;
  readonly SECONDARY_CFG = SCORE_SECONDARY_AXIS_CONFIG;

  keywords: IKeywords[] = [];
  keywordsLoading = false;
  keywordsError = false;
  showKeywordsNoData = false;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId'] || changes['entityids'] || changes['query']) {
      this.reset();
      this.loadHistory();
      // this.loadKeywords();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.history = [];
    this.showScoreChart = false;
    this.showScoreSpinner = false;
    this.scoreLoadingFailed = false;
    this.scoreDataMissing = false;
  }

  get scoreChartTitle(): string {
    return this.secondaryAxis
      ? `Innovation Score vs ${SCORE_SECONDARY_AXIS_CONFIG[this.secondaryAxis].label}`
      : 'Innovation Score';
  }

  toggleScoreSecondaryAxis(axis: Exclude<ScoreSecondaryAxis, null>): void {
    this.secondaryAxis = this.secondaryAxis === axis ? null : axis;
    if (this.history.length) this.buildScoreChart();
  }

  setTimeRange(range: TimeRange): void {
    this.timeRange = range;
    if (this.history.length) this.buildScoreChart();
  }

  resetZoom(): void {
    this.chartDirective?.chart?.resetZoom();
  }

  async downloadPng(): Promise<void> {
    const node = this.exportTarget?.nativeElement;
    if (!node) return;
    const canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
    });
    const safeName = (this.tab_name || 'innovation-activity').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${safeName}-innovation-activity.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private getFilteredHistory(): IInnovationMetricsHistoryPoint[] {
    if (!this.history.length) return this.history;

    let data = this.history;

    if (this.timeRange === '1y') {
      const lastDate = new Date(data[data.length - 1].date_eom);
      const cutoff = new Date(lastDate);
      cutoff.setFullYear(cutoff.getFullYear() - 1);
      return data.filter(p => new Date(p.date_eom) >= cutoff);
    }

    // 3Y + 10Y: quarter-end points only (source data mixes monthly and quarterly,
    // so this guarantees uniform spacing). Always keep the very last point even
    // if it's a non-quarter month — that's the "live" data tip.
    if (this.timeRange === '3y') {
      const lastDate = new Date(data[data.length - 1].date_eom);
      const cutoff = new Date(lastDate);
      cutoff.setFullYear(cutoff.getFullYear() - 3);
      data = data.filter(p => new Date(p.date_eom) >= cutoff);
    }

    const QUARTER_MONTHS = new Set([3, 6, 9, 12]);
    const last = data[data.length - 1];
    const quarterly = data.filter(p => QUARTER_MONTHS.has(new Date(p.date_eom).getMonth() + 1));
    const lastMonth = new Date(last.date_eom).getMonth() + 1;
    if (!QUARTER_MONTHS.has(lastMonth)) quarterly.push(last);
    return quarterly;
  }

  private loadHistory(): void {
    const entityid = this.entityids?.[0];
    if (!entityid || !this.tabId) {
      this.scoreDataMissing = true;
      this.showScoreChart = false;
      return;
    }

    // TODO: remove dummy short-circuit and restore backend call below.
    // this.history = generateDummyInnovationHistory(entityid);
    // this.buildScoreChart();
    // this.showScoreSpinner = false;
    // this.scoreLoadingFailed = false;
    // this.scoreDataMissing = false;
    // return;

    const currentTabId = this.tabId;
    this.showScoreSpinner = true;
    this.scoreLoadingFailed = false;
    this.scoreDataMissing = false;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, 'innov_metrics_history')).pipe(
      take(1),
      switchMap((cached) => {
        if (cached) {
          if (this.tabId === currentTabId) {
            this.history = cached;
            this.buildScoreChart();
            this.showScoreSpinner = false;
          }
          return of(null);
        }
        return this.apiService.getInnovationHistory(entityid!, currentTabId).pipe(
          takeUntil(this.destroy$),
          tap((res) => {
            const payload = res.body?.payload;
            if (!res.ok || !payload?.history?.length) {
              if (this.tabId === currentTabId) {
                this.showScoreSpinner = false;
                this.scoreDataMissing = true;
                this.showScoreChart = false;
              }
              return;
            }
            const targetTabId = payload.tabId ?? currentTabId;
            const historyData = payload.history;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: 'innov_metrics_history', data: historyData }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.showScoreSpinner = false;
              this.history = historyData;
              this.buildScoreChart();
            }
          }),
        );
      }),
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.showScoreSpinner = false;
          this.scoreLoadingFailed = true;
          this.showScoreChart = false;
        }
      },
    });
  }

  private buildScoreChart(): void {
    const filtered = this.getFilteredHistory();
    if (!filtered.length) {
      this.scoreDataMissing = true;
      this.showScoreChart = false;
      return;
    }

    const scorePoints = filtered.map(p => ({ x: toMonthStart(p.date_eom), y: p.innovation_score })) as any;

    const datasets: any[] = [];

    if (this.secondaryAxis) {
      const cfg = SCORE_SECONDARY_AXIS_CONFIG[this.secondaryAxis];
      const secPoints = filtered.map(p => ({
        x: toMonthStart(p.date_eom),
        y: (p[cfg.field] as number | null) ?? null,
      })) as any;

      if (cfg.type === 'bar') {
        datasets.push({
          type: 'bar',
          label: cfg.label,
          data: secPoints,
          yAxisID: 'y2',
          backgroundColor: this.hexWithAlpha(cfg.color, cfg.opacity ?? 0.45),
          borderColor: cfg.color,
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          // 'flex' sizes each bar to its own neighbour gap, not the global
          // smallest gap. Keeps quarter bars wide while the sub-quarter live
          // tip (e.g. Apr next to Mar) narrows to fit — otherwise that one
          // 1-month gap collapses every bar in the series.
          barThickness: 'flex',
          categoryPercentage: 0.85,
          barPercentage: 0.7,
          order: 2,
        });
      } else {
        datasets.push({
          type: 'line',
          label: cfg.label,
          data: secPoints,
          yAxisID: 'y2',
          borderColor: cfg.color,
          borderWidth: 2,
          tension: 0.35,
          cubicInterpolationMode: 'monotone',
          pointRadius: 2,
          pointHoverRadius: 4,
          pointBackgroundColor: cfg.color,
          fill: false,
          order: 2,
        });
      }
    }

    datasets.push({
      type: 'line',
      label: 'Innovation Score',
      data: scorePoints,
      yAxisID: 'y',
      borderColor: COLOR.scoreLine,
      borderWidth: 2,
      tension: 0.35,
      cubicInterpolationMode: 'monotone',
      pointRadius: 0,
      pointHoverRadius: 4,
      pointBackgroundColor: COLOR.scoreLine,
      backgroundColor: 'rgba(30, 41, 59, 0.04)',
      fill: 'origin',
      order: 1,
    });

    this.chartjsData = { datasets } as any;

    const firstDate = new Date(toMonthStart(filtered[0].date_eom));
    const lastDate = new Date(toMonthStart(filtered[filtered.length - 1].date_eom));
    const xMinStr = isoDay(addDays(firstDate, -AXIS_PAD_DAYS));
    const xMaxStr = isoDay(addDays(lastDate, AXIS_PAD_DAYS));
    const xMinMs = new Date(xMinStr).getTime();
    const xMaxMs = new Date(xMaxStr).getTime();

    const yScale: any = {
      position: 'left',
      min: 0,
      max: 100,
      title: {
        display: true,
        text: 'Innovation Score',
        font: { family: FONT_FAMILY, size: 14, weight: 500 },
        color: COLOR.axisTitle,
        padding: { bottom: 8 },
      },
      ticks: {
        font: { family: FONT_FAMILY, size: 14 },
        color: COLOR.axisText,
        padding: 8,
        stepSize: 25,
        callback: (value: number | string) => {
          const v = typeof value === 'number' ? value : Number(value);
          if (v === 25) return 'SELL · 25';
          if (v === 75) return 'BUY · 75';
          if (v === 0 || v === 50 || v === 100) return `${v}`;
          return '';
        },
      },
      grid: { color: COLOR.grid },
      border: { display: false },
    };

    const scales: any = {
      x: {
        type: 'time',
        min: xMinStr,
        max: xMaxStr,
        offset: false,
        time: {
          unit: this.timeRange === '1y' ? 'month' : 'year',
          tooltipFormat: 'MMM yyyy',
          displayFormats: { month: 'MMM yy', quarter: 'QQQ yyyy', year: 'yyyy' },
        },
        ticks: {
          maxRotation: 0,
          font: { family: FONT_FAMILY, size: 14 },
          color: COLOR.axisText,
          padding: 8,
        },
        grid: { display: false },
        border: { display: false },
      },
      y: yScale,
    };

    if (this.secondaryAxis) {
      const cfg = SCORE_SECONDARY_AXIS_CONFIG[this.secondaryAxis];
      const isBounded = this.secondaryAxis === 'quality' || this.secondaryAxis === 'rank';
      scales.y2 = {
        position: 'right',
        min: isBounded ? 0 : undefined,
        max: isBounded ? 100 : undefined,
        title: {
          display: true,
          text: cfg.label,
          font: { family: FONT_FAMILY, size: 14, weight: 500 },
          color: cfg.color,
          padding: { bottom: 8 },
        },
        ticks: {
          maxTicksLimit: 6,
          font: { family: FONT_FAMILY, size: 14 },
          color: cfg.color,
          padding: 8,
        },
        grid: { display: false },
        border: { display: false },
      };
    }

    this.chartjsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 24, right: 32, bottom: 8, left: 8 } },
      animation: {
        duration: 800,
        easing: 'easeOutQuart',
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: COLOR.tooltipBg,
          titleColor: COLOR.tooltipTitle,
          titleFont: { family: FONT_FAMILY, size: 13, weight: 500 },
          bodyColor: COLOR.tooltipBody,
          bodyFont: { family: FONT_FAMILY, size: 14, weight: 600 },
          padding: 12,
          borderColor: COLOR.tooltipBorder,
          borderWidth: 1,
          displayColors: true,
          cornerRadius: 8,
          caretSize: 0,
          callbacks: {
            title: (items: any[]) => {
              const x = items[0].parsed.x ?? 0;
              return new Date(x).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            },
            label: (item: any) => {
              const v = item.parsed.y;
              if (v == null) return '';
              const dsLabel = item.dataset.label ?? '';
              if (dsLabel === 'Innovation Score') {
                return ` ${dsLabel}: ${Number(v).toFixed(1)}`;
              }
              const cfg = this.secondaryAxis ? SCORE_SECONDARY_AXIS_CONFIG[this.secondaryAxis] : null;
              const suffix = cfg?.hoverSuffix ?? '';
              return ` ${dsLabel}: ${Math.round(v)}${suffix}`;
            },
          },
        },
        annotation: {
          annotations: {
            buyLine: {
              type: 'line',
              yMin: 75, yMax: 75,
              yScaleID: 'y',
              borderColor: SIGNAL_COLORS.BUY + '66',
              borderWidth: 1,
              borderDash: [3, 4],
            },
            sellLine: {
              type: 'line',
              yMin: 25, yMax: 25,
              yScaleID: 'y',
              borderColor: SIGNAL_COLORS.SELL + '66',
              borderWidth: 1,
              borderDash: [3, 4],
            },
          },
        },
        zoom: {
          limits: { x: { min: xMinMs, max: xMaxMs, minRange: MIN_ZOOM_RANGE_MS } },
          pan: {
            enabled: true,
            mode: 'x',
            modifierKey: 'shift',
          },
          zoom: {
            wheel: { enabled: false },
            pinch: { enabled: true },
            drag: {
              enabled: true,
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              borderColor: 'rgba(99, 102, 241, 0.6)',
              borderWidth: 1,
            },
            mode: 'x',
          },
        },
      } as any,
      scales,
    };

    this.showScoreChart = true;
  }

  private hexWithAlpha(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // private loadKeywords(): void {
  //   if (!this.tabId) return;

  //   this.keywordsLoading = true;
  //   this.keywordsError = false;
  //   this.showKeywordsNoData = false;

  //   this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, 'top_keywords')).pipe(
  //     take(1),
  //     switchMap((cached) => {
  //       if (cached) {
  //         this.keywords = cached;
  //         this.keywordsLoading = false;
  //         this.showKeywordsNoData = !this.keywords.length;
  //         return of(null);
  //       }
  //       return this.apiService.getKeywords(this.query, this.entityids, this.tabId).pipe(
  //         takeUntil(this.destroy$),
  //         tap((res) => {
  //           this.keywordsLoading = false;
  //           if (res.status === 204 || !res.body?.payload?.results?.length) {
  //             this.keywords = [];
  //             this.showKeywordsNoData = true;
  //             return;
  //           }
  //           const results = res.body.payload.results;
  //           this.store.dispatch(loadSidenavData({ tabId: this.tabId, sidenavKey: 'top_keywords', data: results }));
  //           this.keywords = results;
  //         }),
  //       );
  //     }),
  //   ).subscribe({
  //     error: () => {
  //       this.keywordsLoading = false;
  //       this.keywordsError = true;
  //     },
  //   });
  // }
}
